import React, { useEffect, useMemo, useState, useRef, lazy } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { FileTextIcon, FileIcon, FileSpreadsheetIcon, FileImageIcon, CheckIcon } from 'lucide-react';
import { isMobile } from '../utils/deviceDetection';
import { useInView } from '../utils/useInView';
type IntegrationDestination = {
  id: string;
  name: string;
  imageUrl: string;
  position: {
    x: number;
    y: number;
  };
  color: string;
  lastReceived?: number;
};
type DocumentType = {
  id: string;
  icon: React.ReactNode;
  name: string;
  color: string;
};
type Document = {
  id: number;
  type: DocumentType;
  destinationId: string;
  position: {
    x: number;
    y: number;
  };
  progress: number;
  opacity: number;
  scale: number;
};
export const DocumentStreamVisualization = () => {
  // Use InView to only animate when visible
  const {
    ref: containerRef,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.25,
    rootMargin: '100px'
  });
  // State for tracking active documents
  const [documents, setDocuments] = useState<Document[]>([]);
  // State for tracking which destinations received documents
  const [receivedDocuments, setReceivedDocuments] = useState<Record<string, number>>({});
  // Define the integration destinations with image URLs instead of icons
  const integrationIcons = [{
    id: 'salesforce',
    imageUrl: 'https://i.imgur.com/oyN4NFY.png',
    color: '#00A1E0'
  }, {
    id: 'google-drive',
    imageUrl: 'https://i.imgur.com/uxpvmo1.png',
    color: '#0F9D58'
  }, {
    id: 'microsoft',
    imageUrl: 'https://i.imgur.com/bFk0Bui.png',
    color: '#00A4EF'
  }, {
    id: 'slack',
    imageUrl: 'https://i.imgur.com/PN4yInp.png',
    color: '#4A154B'
  }, {
    id: 'email',
    imageUrl: 'https://i.imgur.com/Uu6BEPR.png',
    color: '#D44638'
  }, {
    id: 'calendar',
    imageUrl: 'https://i.imgur.com/zcFJp3H.png',
    color: '#FF7A59'
  }, {
    id: 'dropbox',
    imageUrl: 'https://i.imgur.com/eVjw13Q.png',
    color: '#0061FF'
  }, {
    id: 'server',
    imageUrl: 'https://i.imgur.com/C6FEJat.png',
    color: '#6264A7'
  }];
  // Reduce number of integration destinations on mobile
  const mobileOptimizedIcons = useMemo(() => {
    return isMobile() ? integrationIcons.slice(0, 5) // Only use 5 icons on mobile
    : integrationIcons;
  }, []);
  // Create integration destinations in a semi-circle arrangement at the top
  const integrationDestinations: IntegrationDestination[] = useMemo(() => {
    return mobileOptimizedIcons.map((icon, index) => {
      // Calculate position on a semi-circle at the top of the container
      const totalIcons = mobileOptimizedIcons.length;
      const angle = Math.PI + Math.PI * (index / (totalIcons - 1)); // π to 2π range for downward arc
      const radius = 35; // Radius of the semi-circle
      const yOffset = 55; // Increased vertical offset to move the arc down from the top
      return {
        id: icon.id,
        name: icon.id.charAt(0).toUpperCase() + icon.id.slice(1).replace('-', ' '),
        imageUrl: icon.imageUrl,
        position: {
          x: 50 + radius * Math.cos(angle),
          y: yOffset + radius * Math.sin(angle) // Increased offset + radius * sin(angle)
        },
        color: icon.color
      };
    });
  }, [mobileOptimizedIcons]);
  // Document types with different colors and icons
  const documentTypes: DocumentType[] = [{
    id: 'contract',
    icon: <FileTextIcon data-id="element-268" />,
    name: 'Contract',
    color: '#2E6BFF'
  }, {
    id: 'invoice',
    icon: <FileSpreadsheetIcon data-id="element-269" />,
    name: 'Invoice',
    color: '#36D1DC'
  }, {
    id: 'id',
    icon: <FileImageIcon data-id="element-270" />,
    name: 'ID Card',
    color: '#0BB489'
  }, {
    id: 'certificate',
    icon: <FileIcon data-id="element-271" />,
    name: 'Certificate',
    color: '#F59E0B'
  }];
  // Generate new documents periodically - only when in view and with mobile optimizations
  useEffect(() => {
    if (!inView) return;
    const maxDocuments = isMobile() ? 6 : 12; // Fewer documents on mobile
    const interval = setInterval(() => {
      if (documents.length < maxDocuments) {
        // Pick a random document type and destination
        const docType = documentTypes[Math.floor(Math.random() * documentTypes.length)];
        const destination = integrationDestinations[Math.floor(Math.random() * integrationDestinations.length)];
        // Create a new document with random starting position at the bottom
        const newDocument = {
          id: Date.now(),
          type: docType,
          destinationId: destination.id,
          position: {
            x: 20 + Math.random() * 60,
            y: 95 // Start at the bottom (95%)
          },
          progress: 5,
          opacity: 0.3,
          scale: 0.7 // Start slightly smaller
        };
        setDocuments(prev => [...prev, newDocument]);
      }
    }, isMobile() ? 1200 : 800); // Slower document generation on mobile
    return () => clearInterval(interval);
  }, [documents.length, inView, integrationDestinations]);
  // Animate documents flowing to their destinations - optimized for mobile
  useEffect(() => {
    if (!inView || documents.length === 0) return;
    const animationInterval = setInterval(() => {
      setDocuments(prev => prev.map(doc => {
        // Find the target destination
        const destination = integrationDestinations.find(d => d.id === doc.destinationId);
        if (!destination) return doc;
        // Update progress - slower on mobile
        const progressIncrement = isMobile() ? 1 : 1.5;
        const newProgress = doc.progress + progressIncrement;
        // Use simpler easing function on mobile
        const progressFactor = isMobile() ? newProgress / 100 // Linear on mobile
        : easeInOut(newProgress / 100); // Fancy easing on desktop
        const newX = doc.position.x + (destination.position.x - doc.position.x) * progressFactor;
        const newY = doc.position.y + (destination.position.y - doc.position.y) * progressFactor;
        // Simpler opacity and scale calculations for mobile
        let newOpacity = doc.opacity;
        let newScale = doc.scale;
        if (isMobile()) {
          // Simpler animation for mobile
          if (newProgress < 20) {
            newOpacity = 0.3 + newProgress / 20 * 0.7;
            newScale = 0.7 + newProgress / 20 * 0.3;
          } else if (newProgress >= 70) {
            const fadeOutProgress = (newProgress - 70) / 30;
            newOpacity = Math.max(0, 1.0 - fadeOutProgress);
            newScale = Math.max(0.2, 1.0 - fadeOutProgress * 0.8);
          } else {
            newOpacity = 1.0;
            newScale = 1.0;
          }
        } else {
          // More complex animation for desktop
          if (newProgress < 20) {
            newOpacity = 0.3 + newProgress / 20 * 0.7;
            newScale = 0.7 + newProgress / 20 * 0.3;
          } else if (newProgress >= 20 && newProgress <= 60) {
            newOpacity = 1.0;
            newScale = 1.0;
          } else {
            const fadeOutProgress = (newProgress - 60) / 40;
            newOpacity = Math.max(0, 1.0 - fadeOutProgress);
            newScale = Math.max(0.2, 1.0 - fadeOutProgress * 0.8);
          }
        }
        // Show checkmark when document is near destination
        if (newProgress >= 80 && newProgress < 90) {
          setReceivedDocuments(prev => ({
            ...prev,
            [doc.destinationId]: Date.now()
          }));
        }
        // If document reached its destination
        if (newProgress >= 100) {
          return {
            ...doc,
            progress: 100,
            position: {
              x: destination.position.x,
              y: destination.position.y
            },
            opacity: 0,
            scale: 0
          };
        }
        return {
          ...doc,
          progress: newProgress,
          position: {
            x: newX,
            y: newY
          },
          opacity: newOpacity,
          scale: newScale
        };
      }));
    }, isMobile() ? 50 : 30); // Slower animation updates on mobile
    return () => clearInterval(animationInterval);
  }, [documents, integrationDestinations, inView]);
  // Remove documents that have reached their destinations
  useEffect(() => {
    if (!inView) return;
    const cleanup = setInterval(() => {
      setDocuments(prev => prev.filter(doc => doc.progress < 100 || doc.opacity > 0));
    }, 50);
    return () => clearInterval(cleanup);
  }, [inView]);
  // Clear received indicators after a delay
  useEffect(() => {
    if (!inView || Object.keys(receivedDocuments).length === 0) return;
    const cleanup = setInterval(() => {
      const now = Date.now();
      const updatedReceived: Record<string, number> = {};
      Object.entries(receivedDocuments).forEach(([id, timestamp]) => {
        const checkmarkDuration = isMobile() ? 1000 : 2000; // Shorter duration on mobile
        if (now - timestamp < checkmarkDuration) {
          updatedReceived[id] = timestamp;
        }
      });
      if (Object.keys(updatedReceived).length !== Object.keys(receivedDocuments).length) {
        setReceivedDocuments(updatedReceived);
      }
    }, 100);
    return () => clearInterval(cleanup);
  }, [receivedDocuments, inView]);
  // Helper function for easeInOut animation
  const easeInOut = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };
  return <div ref={containerRef} className="w-full h-full rounded-[24px] bg-gradient-to-br from-[#f8faff] to-[#f0f5ff] relative overflow-hidden" data-id="element-272">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#f8faff]/80 to-[#e7f2ff]/40 backdrop-blur-[1px]" data-id="element-273"></div>

      {/* Only render animations when in view */}
      {inView && <>
          {/* Cloud upload icon in the middle */}
          <motion.div className="absolute" style={{
        left: '50%',
        top: '55%',
        zIndex: 5,
        willChange: 'opacity'
      }} initial={{
        opacity: 0,
        x: '-50%',
        y: '-50%'
      }} animate={{
        opacity: 0.15,
        x: '-50%',
        y: '-50%'
      }} transition={{
        opacity: {
          duration: 1
        }
      }} data-id="element-274">
            <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#2E6BFF]" data-id="element-275">
              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" data-id="element-276"></path>
              <path d="M12 12v9" data-id="element-277"></path>
              <path d="m16 16-4-4-4 4" data-id="element-278"></path>
            </svg>
          </motion.div>

          {/* Gradient overlay at the bottom to fade in documents */}
          <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-[#f0f5ff] via-[#f0f5ff]/90 to-transparent z-10 pointer-events-none" data-id="element-279"></div>

          {/* Simplified connection lines on mobile */}
          {!isMobile() && <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
        zIndex: 15
      }} data-id="element-280">
              {integrationDestinations.map(destination => {
          // Calculate better control points based on destination position
          const startX = 50;
          const startY = 95;
          const controlX1 = 50;
          const controlY1 = (startY + destination.position.y) / 2;
          const controlX2 = (startX + destination.position.x) / 2;
          const controlY2 = destination.position.y + 10;
          return <motion.path key={`path-${destination.id}`} d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${destination.position.x} ${destination.position.y}`} stroke={`${destination.color}00`} strokeWidth="2" fill="none" strokeDasharray="3,3" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 0
          }} transition={{
            duration: 1.5,
            delay: 0.2
          }} data-id="element-281" />;
        })}
            </svg>}

          {/* Integration destinations - images in semi-circle at the top */}
          {integrationDestinations.map(destination => {
        // Check if this destination has received a document
        const hasReceivedDocument = !!receivedDocuments[destination.id];
        return <motion.div key={destination.id} className="absolute" style={{
          left: `${destination.position.x}%`,
          top: `${destination.position.y}%`,
          zIndex: 20,
          willChange: isMobile() ? 'opacity' : 'transform, opacity'
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: isMobile() ? 0 : [0, -5, 0],
          transition: {
            y: !isMobile() ? {
              repeat: Infinity,
              duration: 3 + Math.random() * 2,
              repeatType: 'reverse',
              ease: 'easeInOut'
            } : undefined,
            opacity: {
              duration: 0.5
            }
          }
        }} data-id="element-282">
                <div className="flex items-center justify-center bg-white rounded-full p-3 shadow-lg border border-[#E7EAF0] transition-all duration-300" style={{
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 4px 12px rgba(46,107,255,0.08)'
          }} data-id="element-283">
                  <motion.div className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300" animate={{
              scale: hasReceivedDocument ? [1, 1.15, 1] : 1
            }} transition={{
              scale: {
                duration: 0.4
              }
            }} data-id="element-284">
                    <img src={destination.imageUrl} alt={destination.name} className="w-6 h-6 object-contain" loading="lazy" data-id="element-285" />
                  </motion.div>
                </div>
              </motion.div>;
      })}

          {/* Animated documents - reduced number on mobile */}
          {documents.map(doc => <motion.div key={doc.id} className="absolute z-30" style={{
        left: `${doc.position.x}%`,
        top: `${doc.position.y}%`,
        opacity: doc.opacity,
        transform: `translate(-50%, -50%) scale(${doc.scale})`,
        willChange: 'transform, opacity'
      }} initial={{
        opacity: 0.3
      }} animate={{
        opacity: doc.opacity
      }} transition={{
        duration: 0.05
      }} data-id="element-286">
              <div className="w-10 h-12 rounded-md flex items-center justify-center relative overflow-hidden shadow-md" style={{
          backgroundColor: 'white',
          border: `1.5px solid ${doc.type.color}`,
          color: doc.type.color
        }} data-id="element-287">
                <div className="absolute top-0 left-0 right-0 h-2" style={{
            backgroundColor: doc.type.color
          }} data-id="element-288"></div>
                <div className="mt-2" data-id="element-289">{doc.type.icon}</div>
              </div>
            </motion.div>)}
        </>}
    </div>;
};